import React, { useCallback, useEffect, useState } from 'react';
import AddressFixer from '../components/AddressFixer';
import { useSignedIn } from '../session';
import { AddressToFix, PostingToDo, SourcingDelivery, StoreDelivery, StoreSummary } from '../types';
import SourcingPanel from './SourcingPanel';

import { API_BASE as BASE } from '../services/apiBase';

/** Osvežava se sam, jer poslovođa ovaj ekran drži otvoren uz posao. */
const REFRESH_MS = 120_000;

interface Props {
  code: string;
  onSignOut: () => void;
}

/**
 * Ekran poslovođe maloprodajnog objekta.
 *
 * Ono što ga se tiče: koje adrese njegov trgovac treba da ispravi, odakle roba
 * ide, koji nalog čeka knjiženje, i šta je od njegove prodaje isporučeno.
 *
 * Redosled je namerno takav: prvo ono što drži robu, pa ono što drži knjigu, pa
 * pogled. Adrese stoje prve jer bez njih roba ne može na turu, a ispraviti ih
 * može samo on — kupac je njegov.
 */
export default function StoreApp({ code, onSignOut }: Props) {
  const signedIn = useSignedIn();
  const [addresses, setAddresses] = useState<AddressToFix[]>([]);
  const [deliveries, setDeliveries] = useState<StoreDelivery[]>([]);
  const [stores, setStores] = useState<StoreSummary[]>([]);
  const [sourcing, setSourcing] = useState<SourcingDelivery[]>([]);
  const [postings, setPostings] = useState<PostingToDo[]>([]);

  /** Adresa koju poslovođa upravo ispravlja. Otvara se jedna po jedna. */
  const [openAddressId, setOpenAddressId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [toFix, mine, summary, where, toPost] = await Promise.all([
        fetch(`${BASE}/api/store/${code}/addresses`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BASE}/api/store/${code}/deliveries`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BASE}/api/store/summary`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BASE}/api/store/${code}/sourcing`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${BASE}/api/store/${code}/postings`, { credentials: 'include' }).then((r) => r.json())
      ]);

      setAddresses(toFix);
      setDeliveries(mine);
      setStores(summary);
      setSourcing(where);
      setPostings(Array.isArray(toPost) ? toPost : []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [code]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const me = stores.find((s) => s.code === code);
  const waiting = deliveries.filter((d) => !d.isOnTour);
  const onTour = deliveries.filter((d) => d.isOnTour);

  return (
    <div className="app">
      <header className="app__header">
        <div>
          <h1>{me?.name ?? `Objekat ${code}`}</h1>
          <p className="muted">Šta čeka isporuku i koje adrese treba ispraviti.</p>
        </div>
        <div className="app__who">
          <span className="muted">{signedIn.displayName}</span>
          <button type="button" className="link" onClick={onSignOut}>Odjavi se</button>
        </div>
      </header>

      {error && <div className="health health--bad">{error}</div>}

      <section className="panel">
        <h2>
          Adrese za ispravku {addresses.length > 0 && <span className="muted">· {addresses.length}</span>}
        </h2>

        {addresses.length === 0 ? (
          <p className="empty">Nijedna adresa sa vaše prodaje ne pravi problem.</p>
        ) : (
          <>
            <p className="note">
              Ove adrese se ne mogu naći na mapi, pa roba ne može na turu. Otvori
              adresu i postavi tačku dok ti je kupac na vezi — tada saznaješ i
              sprat i lift. Tekst adrese ispravi u ERP-u kad stigneš.
            </p>

            <ul className="tofix">
              {addresses.map((address) => (
                <li key={address.id}>
                  <button
                    type="button"
                    className="tofix__open"
                    onClick={() =>
                      setOpenAddressId(openAddressId === address.id ? null : address.id)}
                  >
                    <span className="tofix__address">
                      {address.rawAddress || '(bez ulice)'}, {address.rawCity || '(bez mesta)'}
                      <span className="muted"> · {address.erpDocumentKey}</span>
                    </span>
                    <span className="tofix__chevron">
                      {openAddressId === address.id ? '▾' : '▸'}
                    </span>
                  </button>

                  <p className="tofix__what">
                    {address.unknownPlace
                      ? 'Mesto nije u šifarniku pošta — proveri kako je napisano.'
                      : address.needsHouseNumber
                        ? 'Ulica je nađena, kućni broj nije. Fali ili nije tačan.'
                        : address.note}
                  </p>

                  {address.phones.length > 0 ? (
                    <p className="tofix__phones">
                      {address.phones.map((number) => (
                        <a key={number} href={`tel:${number}`}>{number}</a>
                      ))}
                    </p>
                  ) : (
                    <p className="tofix__nophone">Nema ni telefon — adresa se ne može proveriti.</p>
                  )}

                  {/* Isti alat koji ima operater u magacinu. Poslovođa ga koristi
                      iz drugog razloga: dok razgovara sa kupcem, a ne u nizu. */}
                  {openAddressId === address.id && (
                    <div className="tofix__fix">
                      <AddressFixer
                        address={address}
                        onDone={(id) => {
                          setAddresses((current) => current.filter((x) => x.id !== id));
                          setOpenAddressId(null);
                        }}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="panel">
        <h2>
          Odakle roba ide{' '}
          {sourcing.length > 0 && <span className="muted">· {sourcing.length}</span>}
        </h2>
        <SourcingPanel
          code={code}
          deliveries={sourcing}
          onSaved={() => void load()}
        />
      </section>

      {postings.length > 0 && (
        <section className="panel">
          <h2>
            Nalozi za knjiženje <span className="muted">· {postings.length}</span>
          </h2>

          <p className="note">
            Ovi prenosi su upisani u ERP — nalog postoji i roba je prebačena, ali
            <strong> nije proknjižen</strong>. Knjiženje uradite u ERP-u; aplikacija
            to ne dira, jer taj klik ulazi u glavnu i pomoćne knjige. Kad
            proknjižite, nalog sam nestaje sa ovog spiska.
          </p>

          <ul className="storelist">
            {postings.map((posting) => (
              <li key={posting.documentNumber}>
                <span className="storelist__where">
                  Nalog {posting.documentNumber} <span className="muted">· {posting.operation}</span>
                  <span className="muted">
                    {' '}· {posting.from} → {posting.to} · {posting.articleCode} × {posting.quantity}
                    {posting.documentKey && ` · otpremnica ${posting.documentKey}`}
                  </span>
                </span>

                {posting.missingInErp ? (
                  <span className="flag flag--bad">nema ga u ERP-u</span>
                ) : (
                  <span className={posting.waitingDays > 1 ? 'flag flag--bad' : 'flag'}>
                    {posting.waitingDays === 0
                      ? 'danas'
                      : `${posting.waitingDays} ${posting.waitingDays === 1 ? 'dan' : 'dana'}`}
                  </span>
                )}

                {posting.missingInErp && (
                  <span className="storelist__why muted">
                    Upisali smo ga, a u ERP-u ga nema. Pozovi magacin pre knjiženja.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel">
        <h2>Čeka isporuku {waiting.length > 0 && <span className="muted">· {waiting.length}</span>}</h2>

        {waiting.length === 0 ? (
          <p className="muted">Ništa ne čeka.</p>
        ) : (
          <ul className="storelist">
            {waiting.map((delivery) => (
              <li key={delivery.documentKey}>
                <span className="storelist__where">
                  {delivery.address}, {delivery.city}
                  <span className="muted"> · {delivery.customer}</span>
                </span>
                <span className={delivery.isOverdue ? 'flag flag--bad' : 'flag'}>
                  {delivery.waitingDays} {delivery.waitingDays === 1 ? 'dan' : 'dana'}
                </span>
                {!delivery.canBePlanned && delivery.obstacle && (
                  <span className="storelist__why muted">{delivery.obstacle}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {onTour.length > 0 && (
        <section className="panel">
          <h2>Na turi <span className="muted">· {onTour.length}</span></h2>
          <ul className="storelist">
            {onTour.map((delivery) => (
              <li key={delivery.documentKey}>
                <span className="storelist__where">
                  {delivery.address}, {delivery.city}
                  <span className="muted"> · {delivery.customer}</span>
                </span>
                <span className="flag flag--ok">u vozilu</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
